import CreditCardIcon from "@mui/icons-material/CreditCard";
import {
	Box,
	Button,
	ButtonGroup,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from "@mui/material";
import {
	DataTable,
	DataTableColumn,
	useDataTableStorage,
	useSpringPage,
	useSpringPageable,
	useStorage,
	View,
	ViewHeader,
} from "@variocube/app-ui";
import React, {Fragment, useMemo, useState} from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {Link} from "react-router-dom";
import {CreditCard, CreditCardFilter, useCreditCardApi} from "../../api";
import {FileUploadButton} from "../../component/file-upload-button";
import {useUser} from "../../context/auth";
import {useLocalization} from "../../i18n";
import {formatCreditCardNumber} from "../../utils/formatCreditCardNumber";
import {getUserDisplayName} from "../../utils/user";

export function CreditCardList() {
	const {t} = useLocalization();
	const {queryCreditCards, exportCreditCards, importCreditCards} = useCreditCardApi();
	const user = useUser();

	const [filter, setFilter] = useStorage<CreditCardFilter>("CreditCardFilter", {});
	const storage = useDataTableStorage("CreditCardList", "session");
	const pageable = useSpringPageable(storage);
	const query = useMemo(() => ({...filter, ...pageable}), [filter, pageable]);
	const {result, loading, error, execute} = useAsync(queryCreditCards, [query]);
	const {rows, page} = useSpringPage(result);

	const [importError, setImportError] = useState<any>();

	const columns = useMemo<DataTableColumn<CreditCard>[]>(() => [
		{
			field: "cardNumber",
			label: t("creditCards.cardNumber"),
			component: ({row}) => (
				<Chip
					label={formatCreditCardNumber(row.cardNumber)}
					color="secondary"
					variant="outlined"
					icon={<CreditCardIcon />}
					clickable
					component={Link}
					to={`${row.id}`}
				/>
			),
		},
		{
			field: "creditor.company.companyName",
			label: t("creditors.creditCardProvider"),
			component: ({row}) => (
				<Stack>
					<Box>{row.creditor.companyName}</Box>
					<Typography variant="body2" color="textSecondary">{row.creditor.sapCreditorNumber}</Typography>
				</Stack>
			),
		},
		{
			field: "user.lastname",
			label: t("creditCards.user"),
			component: ({row}) => (
				<Stack>
					<Box>{getUserDisplayName(row.user)}</Box>
					<Typography variant="body2" color="textSecondary">{row.user.email}</Typography>
				</Stack>
			),
		},
		{
			field: "inbox.name",
			label: t("inboxes.singular"),
			component: ({row}) => <span>{row.inbox.name}</span>,
		},
	], [t]);

	function clearImportError() {
		setImportError(undefined);
	}

	const importAction = useAsyncCallback(async (file: File) => {
		clearImportError();
		try {
			await importCreditCards(file);
			await execute(query);
		}
		catch (error) {
			setImportError(error);
		}
	});

	async function handleExportCreditCards() {
		await exportCreditCards();
	}

	return (
		<View>
			<ViewHeader
				title={t("navigation.adminGroup.creditCards")}
				actions={
					<ButtonGroup variant="outlined" disableElevation>
						{user.role === "SuperUser" && (
							<Fragment>
								<Button
									variant="outlined"
									color="inherit"
									onClick={handleExportCreditCards}
								>
									{t("creditCards.export")}
								</Button>
								<FileUploadButton
									label={t("creditCards.import")}
									disabled={importAction.loading}
									onUpload={importAction.execute}
									variant="outlined"
									color="inherit"
								/>
							</Fragment>
						)}
						<Button
							variant="outlined"
							color="primary"
							component={Link}
							to="new"
						>
							{t("creditCards.new.title")}
						</Button>
					</ButtonGroup>
				}
			/>
			<DataTable
				columns={columns}
				rows={rows}
				page={page}
				loading={loading || importAction.loading}
				error={error}
				{...storage}
			/>
			<Dialog
				fullWidth
				maxWidth="sm"
				open={importError}
				onClose={clearImportError}
			>
				<DialogTitle>{t("creditCards.importError")}</DialogTitle>
				<DialogContent>
					<Typography whiteSpace="pre-wrap">
						{importError?.detail}
					</Typography>
				</DialogContent>

				<DialogActions>
					<Button onClick={clearImportError}>{t("actions.close")}</Button>
				</DialogActions>
			</Dialog>
		</View>
	);
}
